import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { UQDataGrid, UQButton, UQModal, UQCard, UQInput, type UQDataGridColumn, useToast } from '@dev/uniq-ui';
import { EmployeeConfig } from '../types/EmployeeConfig.ts';
import { GitLabService } from '../services/GitLabService.ts';
import FullscreenLoader from './FullscreenLoader';

const EmployeeConfigEditor: React.FC = () => {
  const { accounts } = useMsal();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  const [employees, setEmployees] = useState<EmployeeConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<EmployeeConfig>>({});

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const gitLabService = new GitLabService();
      const data = await gitLabService.fetchEmployeeConfig();
      setEmployees(data);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('errors.loadFailed');
      setError(errorMsg);
      console.error('Failed to load employee data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployeeData();
  }, []);

  const startEditing = (employee: EmployeeConfig) => {
    setEditingId(employee.id || null);
    setEditValues(employee);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = () => {
    if (editingId && editValues) {
      setEmployees(prev =>
        prev.map(emp => emp.id === editingId ? { ...emp, ...editValues } : emp)
      );
      setHasChanges(true);
      cancelEditing();
    }
  };

  const renderEditableCell = (employee: EmployeeConfig, field: keyof EmployeeConfig, inputType: 'text' | 'number' = 'text') => {
    if (editingId === employee.id) {
      return (
        <UQInput
          name={field}
          type={inputType}
          value={editValues[field]?.toString() || ''}
          onChange={(e) => {
            const value = e.target.value;
            const finalValue = value ? (inputType === 'number' ? Number(value) : value) : null;
            setEditValues(prev => ({ ...prev, [field]: finalValue as any }));
          }}
        />
      );
    }
    return <span>{employee[field]?.toString() || 'N/A'}</span>;
  };

  const columns: UQDataGridColumn<EmployeeConfig>[] = [
    {
      columnId: 'employee_full_name',
      renderHeaderCell: () => <strong>{t('table.employeeName')}</strong>,
      renderCell: (employee) => <span>{employee.employee_full_name || 'N/A'}</span>,
      minWidth: '200px',
    },
    {
      columnId: 'employee_team',
      renderHeaderCell: () => <strong>{t('table.team')}</strong>,
      renderCell: (employee) => renderEditableCell(employee, 'employee_team'),
      minWidth: '150px',
    },
    {
      columnId: 'employee_target_billable_hours',
      renderHeaderCell: () => <strong>{t('table.targetHours')}</strong>,
      renderCell: (employee) => renderEditableCell(employee, 'employee_target_billable_hours', 'number'),
      minWidth: '120px',
    },
    {
      columnId: 'is_active',
      renderHeaderCell: () => <strong>{t('table.active')}</strong>,
      renderCell: (employee) => (
        <span>{employee.is_active ? t('table.active_yes') : t('table.active_no')}</span>
      ),
      minWidth: '100px',
    },
    {
      columnId: 'actions',
      renderHeaderCell: () => <strong>{t('table.actions')}</strong>,
      renderCell: (employee) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {editingId === employee.id ? (
            <>
              <UQButton onClick={saveEdit} type="primary" size="small">
                {t('actions.save')}
              </UQButton>
              <UQButton onClick={cancelEditing} type="secondary" size="small">
                {t('actions.cancel')}
              </UQButton>
            </>
          ) : (
            <>
              <UQButton onClick={() => startEditing(employee)} type="primary" size="small">
                {t('actions.edit')}
              </UQButton>
            </>
          )}
        </div>
      ),
      minWidth: '220px',
    },
  ];

  const handleSaveChanges = async () => {
    if (accounts.length === 0) {
      addToast({ 
        content: t('auth.mustBeLoggedIn'),
        type: 'warning',
        position: 'bottom-right',
        duration: 3000 
      });
      return;
    }

    try {
      setIsSaving(true);
      const gitLabService = new GitLabService();
      const user = accounts[0];
      
      await gitLabService.saveEmployeeConfig(employees, {
        author: user.name || user.username,
        email: user.username,
      });

      setHasChanges(false);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving changes:', err);
      
      if (err instanceof Error) {
        if (err.message.includes('Keine Berechtigung zum Erstellen von Branches')) {
          setError(t('errors.gitlabPermission', { message: err.message }));
        } else if (err.message.includes('ungültig oder abgelaufen')) {
          setError(t('errors.gitlabTokenInvalid'));
        } else {
          setError(t('errors.genericSave', { message: err.message }));
        }
      } else {
        setError(t('errors.genericFailed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadHours = async () => {
    try {
      setIsDownloading(true);
      const gitLabService = new GitLabService();
      await gitLabService.downloadHoursOverview();
      addToast({ content: t('actions.downloadSuccess'), type: 'success', position: 'bottom-right', duration: 3000 });
    } catch (err) {
      addToast({ content: t('actions.downloadFailed'), type: 'error', position: 'bottom-right', duration: 4000 });
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleResetChanges = () => {
    loadEmployeeData();
    setHasChanges(false);
  };

  if (loading) {
    return <FullscreenLoader message={t('editor.loading')} />;
  }

  if (error) {
    return (
      <div>
        <UQCard className="error-card">
          <h3>{t('errors.error')}</h3>
          <p>{error}</p>
          <div style={{ marginTop: '1rem' }}>
            <UQButton onClick={loadEmployeeData} type="action">
              {t('actions.retry')}
            </UQButton>
          </div>
        </UQCard>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {isSaving && <FullscreenLoader message={t('editor.saving')} animation="cube" />}
      <div style={{ flexShrink: 0, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {t('editor.title')}
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              {t('editor.description')}
            </p>
          </div>
          <UQButton
            onClick={handleDownloadHours}
            type="secondary"
            disabled={isDownloading}
          >
            {isDownloading ? t('actions.downloading') : t('actions.downloadHours')}
          </UQButton>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
        <style>{`
          .sticky-grid .uq-data-grid {
            overflow: visible !important;
          }
          .sticky-grid .uq-data-grid table {
            overflow: visible !important;
          }
          .sticky-grid .uq-data-grid table th {
            position: sticky;
            top: 0;
            z-index: 2;
            background-color: #fafafa;
            box-shadow: 0 1px 0 #a6b4bb;
          }
        `}</style>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
          <UQCard>
            <div 
              ref={tableContainerRef}
              className="sticky-grid"
              style={{ 
                overflow: 'auto',
                maxHeight: 'calc(100vh - 320px)',
              }}
            >
              <UQDataGrid
                items={employees}
                columns={columns}
                getRowId={(employee) => employee.id || `temp-${Math.random()}`}
                rowLines
                emptyMessage={t('editor.noEmployees')}
              />
            </div>
          </UQCard>
        </div>
      </div>

      <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', backgroundColor: 'white', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: '0.875rem' }}>
          {hasChanges ? (
            <span style={{ color: '#d97706', fontWeight: 500 }}>• {t('editor.unsavedStatus')}</span>
          ) : (
            <span style={{ color: '#10b981', fontWeight: 500 }}>{t('editor.savedStatus')}</span>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <UQButton
            onClick={handleResetChanges}
            disabled={!hasChanges}
            type="secondary"
          >
            {t('actions.resetChanges')}
          </UQButton>
          
          <UQButton
            onClick={handleSaveChanges}
            disabled={!hasChanges}
            type="primary"
          >
            {t('actions.saveChanges')}
          </UQButton>
        </div>
      </div>

      {showSuccessModal && (
        <UQModal
          show={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          style="success"
          header={t('success.title')}
          buttonOk={t('actions.ok')}
          onOk={() => setShowSuccessModal(false)}
        >
          <p>{t('success.description')}</p>
        </UQModal>
      )}

    </div>
  );
};

export default EmployeeConfigEditor;
